import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Megaphone, Heart, PlusCircle, Pencil, Trash2, CheckCircle2, Gift, RefreshCw, ImageOff, MapPin, Rocket, Bell } from "lucide-react";
import AnnonceCard from "@/components/annonces/AnnonceCard";

const STATUS_BADGE = {
  published: { l: "Publiée", c: "bg-emerald-100 text-emerald-800" },
  suspended: { l: "Suspendue", c: "bg-amber-100 text-amber-800" },
  refused: { l: "Refusée", c: "bg-rose-100 text-rose-800" },
  sold: { l: "Vendue", c: "bg-slate-200 text-slate-700" },
  given: { l: "Donnée", c: "bg-slate-200 text-slate-700" },
  expired: { l: "Expirée", c: "bg-slate-100 text-slate-500" },
  draft: { l: "Brouillon", c: "bg-slate-100 text-slate-500" },
};

export default function MyAnnonces() {
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState([]);
  const [favs, setFavs] = useState([]);
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const nowIso = new Date().toISOString();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, f, s] = await Promise.all([api.get("/me/annonces"), api.get("/me/annonces-favoris"), api.get("/me/saved-searches")]);
      setMine(m.data || []); setFavs(f.data?.items || []); setSearches(s.data || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [boostFor, setBoostFor] = useState(null);
  const boost = async (a, tier) => {
    try {
      const { data } = await api.post(`/annonces/${a.id}/boost/checkout`, { origin_url: window.location.origin, tier });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Paiement indisponible"); }
  };
  const delSearch = async (s) => {
    await api.delete(`/me/saved-searches/${s.id}`); toast.success("Alerte supprimée");
    setSearches((x) => x.filter((y) => y.id !== s.id));
  };

  const act = async (a, action) => {
    try { await api.post(`/annonces/${a.id}/status`, { action }); toast.success("Annonce mise à jour"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
  };
  const del = async (a) => {
    if (!window.confirm(`Supprimer l'annonce « ${a.title} » ?`)) return;
    try { await api.delete(`/annonces/${a.id}`); toast.success("Annonce supprimée"); load(); }
    catch { toast.error("Suppression impossible"); }
  };

  return (
    <div data-testid="my-annonces-page">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2"><Megaphone className="text-[#E05638]" size={24} /> Mes annonces</h1>
        <Link to="/petites-annonces/deposer" className="inline-flex items-center gap-1 btn-shine text-white font-semibold px-4 py-2 rounded-xl text-sm" data-testid="my-annonces-new"><PlusCircle size={16} /> Déposer</Link>
      </div>

      <div className="flex gap-2 mb-5 border-b border-slate-200">
        <button onClick={() => setTab("mine")} data-testid="my-annonces-tab-mine" className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "mine" ? "border-[#E05638] text-[#E05638]" : "border-transparent text-slate-500"}`}>Mes dépôts ({mine.length})</button>
        <button onClick={() => setTab("favs")} data-testid="my-annonces-tab-favs" className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px inline-flex items-center gap-1 ${tab === "favs" ? "border-[#E05638] text-[#E05638]" : "border-transparent text-slate-500"}`}><Heart size={14} /> Favoris ({favs.length})</button>
        <button onClick={() => setTab("alerts")} data-testid="my-annonces-tab-alerts" className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px inline-flex items-center gap-1 ${tab === "alerts" ? "border-[#E05638] text-[#E05638]" : "border-transparent text-slate-500"}`}><Bell size={14} /> Alertes ({searches.length})</button>
      </div>

      <Dialog open={!!boostFor} onOpenChange={(o) => !o && setBoostFor(null)}>
        <DialogContent data-testid="boost-dialog">
          <DialogHeader><DialogTitle>Mettre mon annonce en avant</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Votre annonce apparaît en tête de liste avec un badge « À la une ».</p>
          <div className="space-y-2">
            <button onClick={() => boost(boostFor, "standard")} className="w-full text-left border border-slate-200 rounded-xl p-3 hover:border-[#E05638] transition-colors" data-testid="boost-tier-standard">
              <div className="font-bold text-slate-900">2 € <span className="font-normal text-slate-500">· 7 jours</span></div>
              <div className="text-xs text-slate-500">En tête de liste pendant 7 jours</div>
            </button>
            <button onClick={() => boost(boostFor, "premium")} className="w-full text-left border-2 border-amber-300 bg-amber-50 rounded-xl p-3 hover:border-amber-500 transition-colors relative" data-testid="boost-tier-premium">
              <span className="absolute top-2 right-2 text-[10px] font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full">POPULAIRE</span>
              <div className="font-bold text-slate-900">3,50 € <span className="font-normal text-slate-500">· 15 jours</span></div>
              <div className="text-xs text-slate-600">Visibilité maximale, deux fois plus longtemps</div>
            </button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setBoostFor(null)}>Annuler</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {loading && <p className="text-slate-500 text-sm">Chargement…</p>}

      {!loading && tab === "mine" && (
        mine.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center" data-testid="my-annonces-empty">
            <Megaphone className="mx-auto text-slate-300" size={40} />
            <div className="mt-2 text-slate-500 text-sm">Vous n'avez pas encore d'annonce.</div>
            <Link to="/petites-annonces/deposer" className="mt-3 inline-block text-[#E05638] font-semibold hover:underline">Déposer ma première annonce</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {mine.map((a) => {
              const b = STATUS_BADGE[a.status] || { l: a.status, c: "bg-slate-100 text-slate-600" };
              return (
                <div key={a.id} className="bg-white border border-slate-200 rounded-2xl p-3 flex gap-3" data-testid={`my-annonce-${a.id}`}>
                  <Link to={`/petites-annonces/annonce/${a.slug}`} className="w-20 h-20 rounded-xl bg-slate-100 overflow-hidden shrink-0 grid place-items-center">
                    {a.main_image ? <img src={a.main_image} alt="" className="w-full h-full object-cover" /> : <ImageOff size={22} className="text-slate-300" />}
                  </Link>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <Link to={`/petites-annonces/annonce/${a.slug}`} className="font-semibold text-slate-900 text-sm line-clamp-1 hover:text-[#E05638]">{a.title}</Link>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${b.c}`} data-testid={`my-annonce-status-${a.id}`}>{b.l}</span>
                    </div>
                    <div className="text-[#E05638] font-bold text-sm mt-0.5">{a.price_label}</div>
                    <div className="text-xs text-slate-500 inline-flex items-center gap-1"><MapPin size={11} /> {a.city}</div>
                    {a.moderation_reason && (a.status === "suspended" || a.status === "refused") && (
                      <div className="mt-1 text-xs text-rose-600">Motif modération : {a.moderation_reason}. Modifiez votre annonce pour la rendre conforme.</div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <Link to={`/petites-annonces/modifier/${a.id}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-slate-200 hover:border-slate-400" data-testid={`my-annonce-edit-${a.id}`}><Pencil size={12} /> Modifier</Link>
                      {a.status === "published" && <>
                        <button onClick={() => act(a, "sold")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-slate-200 hover:border-slate-400" data-testid={`my-annonce-sold-${a.id}`}><CheckCircle2 size={12} /> Vendu</button>
                        <button onClick={() => act(a, "given")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-slate-200 hover:border-slate-400" data-testid={`my-annonce-given-${a.id}`}><Gift size={12} /> Donné</button>
                        {a.boosted_until && a.boosted_until > nowIso ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-amber-100 text-amber-800 font-semibold" data-testid={`my-annonce-boosted-${a.id}`}><Rocket size={12} /> À la une</span>
                        ) : (
                          <button onClick={() => setBoostFor(a)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 font-semibold" data-testid={`my-annonce-boost-${a.id}`}><Rocket size={12} /> Remonter</button>
                        )}
                      </>}
                      {(a.status === "expired" || a.status === "sold" || a.status === "given") && (
                        <button onClick={() => act(a, "republish")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`my-annonce-renew-${a.id}`}><RefreshCw size={12} /> Republier</button>
                      )}
                      <button onClick={() => del(a)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`my-annonce-delete-${a.id}`}><Trash2 size={12} /> Supprimer</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {!loading && tab === "favs" && (
        favs.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center" data-testid="my-annonces-favs-empty">
            <Heart className="mx-auto text-slate-300" size={40} />
            <div className="mt-2 text-slate-500 text-sm">Aucune annonce en favori.</div>
            <Link to="/petites-annonces" className="mt-3 inline-block text-[#E05638] font-semibold hover:underline">Parcourir les annonces</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="my-annonces-favs-grid">
            {favs.map((a) => <AnnonceCard key={a.id} a={a} testIdPrefix="fav-annonce" />)}
          </div>
        )
      )}

      {!loading && tab === "alerts" && (
        searches.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center" data-testid="my-annonces-alerts-empty">
            <Bell className="mx-auto text-slate-300" size={40} />
            <div className="mt-2 text-slate-500 text-sm">Aucune alerte enregistrée.</div>
            <Link to="/petites-annonces" className="mt-3 inline-block text-[#E05638] font-semibold hover:underline">Créer une alerte depuis la recherche</Link>
          </div>
        ) : (
          <div className="space-y-2" data-testid="my-annonces-alerts-list">
            <p className="text-xs text-slate-500">Vous recevez un résumé quotidien par e-mail dès qu'une nouvelle annonce correspond.</p>
            {searches.map((s) => (
              <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-3" data-testid={`alert-${s.id}`}>
                <div className="min-w-0 text-sm">
                  <span className="font-semibold text-slate-900">{s.q || s.category_name || "Toutes les annonces"}</span>
                  <div className="text-xs text-slate-500">
                    {[s.category_name, s.city, s.free_only ? "Gratuit" : "", (s.price_min || s.price_max) ? `${s.price_min || 0}–${s.price_max || "∞"} €` : ""].filter(Boolean).join(" · ") || "Aucun filtre"}
                  </div>
                </div>
                <button onClick={() => delSearch(s)} className="shrink-0 text-xs px-2 py-1 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1" data-testid={`alert-del-${s.id}`}><Trash2 size={12} /> Supprimer</button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
